/**
 * Adaptive grid: alignment guides whose spacing adapts to the zoom level,
 * like Endless Paper's "adaptive bullet grid" and InfiniPaint's infinite
 * square grids. Lines lie on cell boundaries, so they are exact at every
 * depth (powers of two in the cell tree).
 *
 * Also draws the world axes (x=0 / y=0) slightly emphasized.
 */

import { Camera } from "./camera";

export interface GridOptions {
  color: string;
  minorColor: string;
  axisColor: string;
  /** Screen-space spacing window (px) for grid lines. */
  minSpacingPx: number;
  maxSpacingPx: number;
  enabled: boolean;
}

export const DEFAULT_GRID: GridOptions = {
  color: "rgba(120,120,120,0.10)",
  minorColor: "rgba(120,120,120,0.06)",
  axisColor: "transparent",
  minSpacingPx: 24,
  maxSpacingPx: 480,
  enabled: false,
};

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  w: number,
  h: number,
  dpr: number,
  opts: GridOptions = DEFAULT_GRID,
): void {
  if (!opts.enabled) return;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const cx = w / 2;
  const cy = h / 2;

  // In focus-cell space, a cell at depth d has size 2^(D-d); its boundaries
  // lie on lines x = k * 2^(D-d-1) ... rendered only when screen spacing is
  // inside the window. D = camera.depth.
  const D = camera.depth;
  const scale = camera.scale; // px per focus unit

  // Gather levels: spacing in focus units s_u = 2^(D-d); screen s_px = s_u*scale.
  const levels: Array<{ d: number; spPx: number }> = [];
  for (let d = D; d >= 0 && d >= D - 30; d--) {
    const spPx = Math.pow(2, D - d) * scale;
    if (spPx > opts.maxSpacingPx * 4) break;
    levels.push({ d, spPx });
  }

  const focus = { x: camera.x, y: camera.y };
  const rot = camera.rotation;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rot);
  ctx.scale(scale, scale);
  ctx.translate(-focus.x, -focus.y);

  const halfW = (w / 2 / scale) * 1.2;
  const halfH = (h / 2 / scale) * 1.2;

  for (const { d, spPx } of levels) {
    const inWindow = spPx >= opts.minSpacingPx && spPx <= opts.maxSpacingPx;
    if (!inWindow) continue;
    const spacing = Math.pow(2, D - d);
    const half = spacing / 2;
    ctx.strokeStyle = d === D ? opts.color : opts.minorColor;
    ctx.lineWidth = 1 / scale;
    ctx.beginPath();
    // Vertical lines at x = (2k+1)*half
    const kMinX = Math.floor((focus.x - halfW) / spacing);
    const kMaxX = Math.ceil((focus.x + halfW) / spacing);
    for (let k = kMinX; k <= kMaxX; k++) {
      const x = (k + 0.5) * spacing;
      ctx.moveTo(x, focus.y - halfH);
      ctx.lineTo(x, focus.y + halfH);
    }
    const kMinY = Math.floor((focus.y - halfH) / spacing);
    const kMaxY = Math.ceil((focus.y + halfH) / spacing);
    for (let k = kMinY; k <= kMaxY; k++) {
      const y = (k + 0.5) * spacing;
      ctx.moveTo(focus.x - halfW, y);
      ctx.lineTo(focus.x + halfW, y);
    }
    ctx.stroke();
  }

  // Axes (x=0 and y=0 of the focus cell).
  ctx.strokeStyle = opts.axisColor;
  ctx.lineWidth = 1.5 / scale;
  ctx.beginPath();
  ctx.moveTo(0, focus.y - halfH);
  ctx.lineTo(0, focus.y + halfH);
  ctx.moveTo(focus.x - halfW, 0);
  ctx.lineTo(focus.x + halfW, 0);
  ctx.stroke();

  ctx.restore();
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
}
